/**
 * Environment bootstrap API.
 *
 * V2: GET /v2/data/services (no env, no summary) — returns all services for the
 *     tenant across all environments. Rows are grouped by environment to produce
 *     the { name, apps } shape the EnvironmentContext expects.
 *
 * Using the same v2 services inventory as the Home cards ensures the env dropdown
 * and app dropdown are always derived from the same source of truth.
 */

/**
 * Group a flat v2 services list into per-environment buckets.
 * Exported for unit testing without a live API.
 *
 * @param {Array} services - rows from /v2/data/services response (.data)
 * @returns {{ name: string, apps: { id: string, name: string }[] }[]}
 */
export function groupServicesByEnvironment(services) {
  if (!Array.isArray(services)) return [];
  const byEnv = {};
  for (const svc of services) {
    const env = svc.environment || '';
    if (!env) continue;
    if (!byEnv[env]) byEnv[env] = { name: env, apps: [] };
    byEnv[env].apps.push({ id: String(svc.service_id), name: svc.service_name });
  }
  return Object.values(byEnv);
}

/**
 * Fetch environment + app inventory for the current tenant.
 *
 * Always returns a promise resolving to { environments: [{ name, apps }] }.
 *
 * @param {string} sessionJwt - Stytch session JWT
 * @returns {Promise<{ environments: { name: string, apps: { id: string, name: string }[] }[] }>}
 */
export async function fetchEnvironmentsData(sessionJwt) {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  const headers = {
    'Authorization': `Bearer ${sessionJwt}`,
    'Content-Type': 'application/json',
  };

  const resp = await fetch(`${apiUrl}/v2/data/services?limit=500`, { headers });
  if (!resp.ok) throw new Error(`HTTP error! status: ${resp.status}`);
  const json = await resp.json();
  const services = Array.isArray(json.data) ? json.data : [];
  return { environments: groupServicesByEnvironment(services) };
}
